import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Icon,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { QueryStats, Visibility } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { toastWarning } from 'utils/toast';
import { dropTipoPessoa } from 'utils/drops';
import { summarizer, findOnArray, decryptURL } from 'utils/functions';
import { RemessasContext } from 'contexts/RemessasContext';
import { DropsContext } from 'contexts/DropsContext';
import { useDialog } from 'components/Modals';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Header from 'components/Header';
import Loader from 'components/Loader';
import Card from 'components/Card';
import styles from './styles';
import ItemModal from './Modals/ItemModal';
import ResumoModal from './Modals/ResumoModal';

const Bonus = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const [step, setStep] = useState(0);
  const decrypt = decryptURL(hash);
  const defaultValues = {
    //CLIENTE
    pessoa: 'FISICA',
    cadastro_id: null,
    cnpj: '',
    cpf: '',
    //ITENS
    itens: [],
  };
  const { control, setValue, getValues, watch, handleSubmit } = useForm({
    defaultValues,
  });
  const { drops } = useContext(DropsContext);
  const { getItens, getLoading } = useContext(RemessasContext);
  const { openDialog, closeDialog } = useDialog();

  useEffect(() => {
    if (step === 0) {
      setValue('itens', []);
    }
  }, [step]);

  useEffect(() => {
    if (decrypt?.cadastro_id) {
      setValue('cadastro_id', decrypt?.cadastro_id);
      const documento = findOnArray(
        decrypt?.cadastro_id,
        drops?.Entidade,
        'documento'
      );
      searchDocumento(documento);
    }
  }, []);

  const onCellEditCommit = (e) => {
    const editedRows = watch('itens')?.map((c) => {
      let newItem = { ...c };

      if (c?.id === e?.id && e?.field === 'quantidade') {
        if (e?.value > c?.quantidade_max) {
          newItem = { ...c, [e?.field]: c?.quantidade_max };
        } else {
          newItem = { ...c, [e?.field]: e?.value };
        }
      }

      const desconto_vlr =
        newItem?.desconto1_pc * newItem?.preco * newItem?.quantidade;

      newItem = {
        ...newItem,
        desconto_vlr: desconto_vlr !== 0 ? desconto_vlr * -0.01 : desconto_vlr,
      };
      const subtotal =
        (newItem?.preco || 0) * (newItem?.quantidade || 0) +
        (newItem?.desconto_vlr || 0);
      newItem = { ...newItem, subtotal };
      return newItem;
    });
    setValue('itens', editedRows);
  };

  const onEditItem = ({ id, ...rest }) => {
    const { itens } = getValues();
    const preco = rest?.preco || 0;
    const quantidade = rest?.quantidade || 0;
    const desconto_vlr = rest?.desconto_vlr || 0;
    const desconto1_pc = rest?.desconto1_pc || 0;
    const quantidade_max = rest?.quantidade_max || 0;
    if (quantidade > quantidade_max) {
      return toastWarning(
        `Quantidade limite excedida. (Limite: ${quantidade_max})`
      );
    }
    if (quantidade <= 0) {
      onDeleteItem(id);
    } else {
      setValue(
        'itens',
        itens?.map((item) => {
          if (item?.id === id) {
            const subtotal = quantidade * preco + desconto_vlr;
            return {
              ...item,
              preco,
              quantidade,
              desconto_vlr,
              desconto1_pc,
              subtotal,
            };
          }
          return item;
        })
      );
    }
    closeDialog();
  };

  const onDeleteItem = (id) => {
    const itens = watch('itens')?.filter((i) => i?.id !== id);
    setValue('itens', itens);
  };

  const onChangeStep = (values) => {
    getItens({
      values: { itens: decrypt?.itens, cadastro_id: values?.cadastro_id },
      cb: (data) => {
        const itens = data
          ?.map((item) => {
            const gerados = item?.Vinculos?.filter(
              (v) =>
                v?.natureza_operacao_id === 54 ||
                ((v?.especie === 'FATURA' || v?.especie === 'ESTOQUE') &&
                  v?.terceiro === 'SIM')
            );
            const quantidade =
              (item?.quantidade || 0) - summarizer(gerados, 'quantidade');
            item['quantidade'] = quantidade;
            item['quantidade_max'] = quantidade;
            item['subtotal'] =
              (item?.quantidade || 0) * (item?.preco || 0) +
              (item?.desconto_vlr || 0);
            delete item?.Vinculos;
            return item;
          })
          ?.filter((f) => Boolean(f?.quantidade));
        setValue('itens', itens);
        setStep(1);
      },
    });
  };

  const onSubmit = (values) => {
    if (!Boolean(values?.itens?.length)) {
      return toastWarning('Insira ao menos um item');
    }
    openDialog(
      <ResumoModal
        values={values}
        vendedor_id={decrypt?.vendedor_id}
        onReset={() => {
          closeDialog();
          navigate(-1);
        }}
      />,
      'Resumo da Bonificação'
    );
  };

  const searchCliente = ({ value, size }) => {
    if (value?.length === size) {
      const entidade = drops?.Entidade.find(
        (f) => f?.documento == value?.replace(/[./-]/g, '')
      );
      setValue('cadastro_id', entidade?.value || null);
    } else {
      setValue('cadastro_id', null);
    }
  };

  const searchDocumento = (numero) => {
    let documento = numero?.replace(/\D/g, '');
    if (documento?.length === 11) {
      documento = documento
        ?.replace(/(\d{3})(\d)/, '$1.$2')
        ?.replace(/(\d{3})(\d)/, '$1.$2')
        ?.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      setValue('pessoa', 'FISICA');
      setValue('cpf', documento);
      setValue('cnpj', '');
    } else if (documento?.length === 14) {
      documento = documento
        ?.replace(/^(\d{2})(\d)/, '$1.$2')
        ?.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        ?.replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
      setValue('pessoa', 'JURIDICA');
      setValue('cnpj', documento);
      setValue('cpf', '');
    }
  };

  return (
    <Container>
      <Header titulo="Bonificação" />
      <Card>
        <Stepper activeStep={step} sx={styles?.stepper}>
          <Step>
            <StepLabel>Cliente</StepLabel>
          </Step>
          <Step>
            <StepLabel>Itens</StepLabel>
          </Step>
        </Stepper>
        {step === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Dropdown
                name="pessoa"
                control={control}
                label="Tipo de Pessoa"
                options={dropTipoPessoa}
                onValueChange={() => {
                  setValue('cpf', '');
                  setValue('cnpj', '');
                  setValue('cadastro_id', null);
                }}
              />
            </Grid>
            {watch('pessoa') === 'FISICA' && (
              <Grid item xs={12} sm={6}>
                <InputMask
                  name="cpf"
                  control={control}
                  label="CPF"
                  mask="999.999.999-99"
                  onKeyUp={(e) => {
                    const value = e?.target?.value;
                    searchCliente({ value, size: 14 });
                  }}
                />
              </Grid>
            )}
            {watch('pessoa') === 'JURIDICA' && (
              <Grid item xs={12} sm={6}>
                <InputMask
                  name="cnpj"
                  control={control}
                  label="CNPJ"
                  mask="99.999.999/9999-99"
                  onKeyUp={(e) => {
                    const value = e?.target?.value;
                    searchCliente({ value, size: 18 });
                  }}
                />
              </Grid>
            )}
            <Grid item xs={10}>
              <VirtualDrop
                name="cadastro_id"
                control={control}
                label="Cliente"
                options={drops?.Entidade}
                refresh="Entidade"
                onValueChange={(v) => searchDocumento(v?.documento)}
              />
            </Grid>
            <Grid
              item
              xs={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <IconButton
                color="primary"
                size="small"
                onClick={() =>
                  window.open(
                    `https://cadastros.eprom.com.br/app/Entidades/Detalhes/${watch(
                      'cadastro_id'
                    )}`
                  )
                }
                disabled={!Boolean(watch('cadastro_id'))}
              >
                <Visibility fontSize="small" />
              </IconButton>
              <IconButton
                color="primary"
                size="small"
                onClick={() =>
                  window.open(
                    `https://cadastros.eprom.com.br/app/Entidades/Indicadores/${watch(
                      'cadastro_id'
                    )}`
                  )
                }
                disabled={!Boolean(watch('cadastro_id'))}
              >
                <QueryStats fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item xs={12} {...styles?.buttonContainer}>
              <Button
                variant="outlined"
                color="primary"
                sx={styles?.button}
                loading={getLoading}
                onClick={handleSubmit(onChangeStep)}
              >
                PRÓXIMO
              </Button>
            </Grid>
          </Grid>
        )}
        {step === 1 && getLoading && <Loader />}
        {step === 1 && !getLoading && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DataGrid
                rows={watch('itens')}
                columns={[
                  {
                    field: 'referencia',
                    headerName: 'Referência',
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'descricao',
                    headerName: 'Produto',
                    width: 440,
                    sortable: false,
                  },
                  {
                    field: 'quantidade',
                    headerName: 'Quantidade',
                    width: 200,
                    type: 'number',
                    sortable: false,
                    editable: true,
                  },
                  {
                    field: 'preco',
                    headerName: 'Preço Unitário',
                    type: 'number',
                    valueGetter: ({ value }) =>
                      (value || 0)?.toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'valor',
                    headerName: 'Valor',
                    type: 'number',
                    valueGetter: ({ row }) =>
                      (
                        (row?.quantidade || 0) * (row?.preco || 0)
                      )?.toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'desconto_vlr',
                    headerName: 'Desconto',
                    type: 'number',
                    valueGetter: ({ value }) =>
                      (value || 0)?.toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                    width: 200,
                    sortable: false,
                  },

                  {
                    field: 'subtotal',
                    headerName: 'Subtotal',
                    type: 'number',
                    valueGetter: ({ value }) =>
                      (value || 0)?.toLocaleString('pt-br', {
                        style: 'currency',
                        currency: 'BRL',
                      }),
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'actions',
                    headerName: 'Ações',
                    type: 'actions',
                    width: 200,

                    getActions: ({ id, row }) => [
                      <GridActionsCellItem
                        icon={<Icon>delete</Icon>}
                        label="Excluir"
                        onClick={() => onDeleteItem(id)}
                      />,
                      <GridActionsCellItem
                        icon={<Icon>edit</Icon>}
                        label="Editar"
                        onClick={() =>
                          openDialog(
                            <ItemModal item={row} onSubmit={onEditItem} />,
                            'Editar Item'
                          )
                        }
                      />,
                    ],
                  },
                ]}
                hideFooter
                autoHeight
                density="compact"
                disableSelectionOnClick
                disableColumnMenu
                showCellRightBorder
                showColumnRightBorder
                onCellEditCommit={onCellEditCommit}
                localeText={{ noRowsLabel: 'Nenhum Registro.' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" align="center">
                Total:{' '}
                {summarizer(watch('itens'), 'subtotal')?.toLocaleString(
                  'pt-br',
                  {
                    style: 'currency',
                    currency: 'BRL',
                  }
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} {...styles?.buttonContainer}>
              <Button
                variant="outlined"
                color="secondary"
                sx={styles?.button}
                onClick={() => setStep(0)}
              >
                VOLTAR
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={styles?.button}
                onClick={handleSubmit(onSubmit)}
              >
                FINALIZAR
              </Button>
            </Grid>
          </Grid>
        )}
      </Card>
    </Container>
  );
};

export default Bonus;
